import { Link } from 'react-router-dom';
import { MapPin, Star, Clock } from 'lucide-react';
import type { Tool } from '@tools-beaver/shared';

interface ToolCardProps {
  tool: Tool;
  compact?: boolean;
}

export function ToolCard({ tool, compact = false }: ToolCardProps) {
  const thumbnail = tool.images?.[0]?.url;
  const priceDisplay = `$${Number(tool.pricePerDay).toFixed(0)}/day`;

  if (compact) {
    return (
      <Link
        to={`/tools/${tool.id}`}
        className="flex items-center gap-3 p-2 rounded-xl hover:bg-silver-50 transition-colors"
      >
        <div className="w-12 h-12 rounded-xl bg-silver-100 flex-shrink-0 overflow-hidden">
          {thumbnail ? (
            <img src={thumbnail} alt={tool.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xl">🔧</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-silver-800 text-sm truncate">{tool.name}</p>
          <p className="text-garden-600 font-bold text-xs">{priceDisplay}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/tools/${tool.id}`} className="card group block">
      {/* Image */}
      <div className="relative h-44 bg-silver-100 overflow-hidden">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={tool.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl text-silver-300">
            🔧
          </div>
        )}
        <div className="absolute top-3 right-3">
          <span className="badge bg-garden-500 text-white font-bold text-sm px-3 py-1">
            {priceDisplay}
          </span>
        </div>
        {tool.condition && (
          <div className="absolute top-3 left-3">
            <span className="badge bg-white/90 text-silver-700 backdrop-blur-sm">
              {tool.condition.replace('_', ' ')}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-silver-800 text-base leading-snug mb-1 line-clamp-2">
          {tool.name}
        </h3>
        {tool.brand && (
          <p className="text-silver-400 text-xs mb-2">{tool.brand}</p>
        )}

        <div className="flex items-center justify-between mt-3">
          {/* Owner rating */}
          {tool.owner && (
            <div className="flex items-center gap-1 text-xs text-silver-500">
              <Star size={12} className="text-beaver-yellow fill-beaver-yellow" />
              <span className="font-medium">{Number(tool.owner.rating).toFixed(1)}</span>
              <span>({tool.owner.reviewCount})</span>
            </div>
          )}

          {/* Location */}
          {tool.city && (
            <div className="flex items-center gap-1 text-xs text-silver-400">
              <MapPin size={12} />
              <span>{tool.city}</span>
            </div>
          )}

          {/* Distance */}
          {tool.distanceMiles !== undefined && (
            <div className="flex items-center gap-1 text-xs text-garden-600 font-medium">
              <Clock size={12} />
              <span>{tool.distanceMiles.toFixed(1)} mi away</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
